/**
 * Warns before saving an invoice edit that changes IssueDate to a different month/year
 * than the one already encoded in InvoiceNumber. Material-UI Dialog modeled on
 * ExpenseInvoices/Components/DeleteConfirmationModal.tsx.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import dayjs from 'dayjs';

interface InvoiceDateDriftWarningDialogProps {
  open: boolean;
  invoiceNumber: string;
  newIssueDate: string;
  onConfirm: () => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const InvoiceDateDriftWarningDialog: React.FC<InvoiceDateDriftWarningDialogProps> = ({
  open,
  invoiceNumber,
  newIssueDate,
  onConfirm,
  onCancel,
  isSubmitting = false,
}) => {
  const formattedNewIssueDate = newIssueDate ? dayjs(newIssueDate).format('DD.MM.YYYY') : '';

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onCancel}
      aria-labelledby="date-drift-dialog-title"
      aria-describedby="date-drift-dialog-description"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="date-drift-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color="warning" />
        Zmiana miesiąca daty wystawienia
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="date-drift-dialog-description">
          Numer faktury <strong>{invoiceNumber}</strong> zawiera już inny miesiąc/rok niż wybrana
          data wystawienia <strong>{formattedNewIssueDate}</strong>. Numer faktury{' '}
          <strong>nie zostanie zmieniony</strong>.
          <br />
          <br />
          Jeśli ta faktura została już zgłoszona do KSeF, tej rozbieżności nie da się później
          skorygować. Kolejna faktura wystawiona w nowym miesiącu będzie miała lukę w numeracji.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isSubmitting} color="inherit">
          Anuluj
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isSubmitting}
          color="warning"
          variant="contained"
          autoFocus
        >
          {isSubmitting ? 'Zapisywanie...' : 'Zapisz mimo to'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
